// Shipping Schemas
import PickupSchema from "./shipping/pickup-schema.json"
import FreeShippingSchema from "./shipping/free-shipping-schema.json"
import StandardShippingSchema from "./shipping/standard-shipping-schema.json"
import ExpressShippingSchema from "./shipping/express-shipping-schema.json"

const rootDomain = "https://www.attitude.se"

const itemListSchema = (props) => {
  const { url, itemsArray } = props;
  if (itemsArray.length > 0) return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "url": url,
    "numberOfItems": itemsArray.length,
    "itemListElement": itemsArray.map((obj, i) => {
      if (obj?.name && obj?.url && obj?.image && obj?.price) return {
        "@type": "Product",
        "position": i + 1,
        "url": obj.url ? rootDomain + obj.url : "",
        "name": obj.name ? obj.name : "",
        "description": obj.description ? obj.description : "",
        "image": {
          "@type": "ImageObject",
          "url": obj.image,
          "contentUrl": obj.image,
          "caption": obj.name
        },
        "offers": {
          "@type": "Offer",
          "price": obj.price ? obj.price : "",
          "priceCurrency": "SEK",
          "availability": "https://schema.org/InStock",
          "url": obj.url ? rootDomain + obj.url : "",
          "priceValidUntil": obj.priceValidUntil ? obj.priceValidUntil : new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString()
        },
        "aggregateRating": obj.aggregateRating,
        "review": obj.review
      }
    }).filter(Boolean) // Ensure no undefined items are included
  }
  else return null;
};

const SiteNavigationSchema = (props) => {
  const { categoryArray } = props
  if (categoryArray.length > 0) return {
    "@context": "https://schema.org",
    "@graph": categoryArray.map((obj) => {
      return {
        "@context": "https://schema.org",
        "@type": "SiteNavigationElement",
        "@id": rootDomain + "#HeaderNavigation",
        "name": obj.name ? obj.name : "",
        "url": obj.url ? rootDomain + obj.url : ""
      }
    })
  }
  else return null
}

const BreadcrumbSchema = (props) => {
  const { url, crumbArray } = props
  const itemListArray = [{
    "@type": "ListItem",
    "@id": rootDomain,
    "position": 1,
    "name": "Hem",
    "item": rootDomain + "/",
  }]
  if (crumbArray.length > 0) crumbArray.forEach((obj, i) => {
    itemListArray.push({
      "@type": "ListItem",
      "@id": obj.url ? rootDomain + obj.url : "",
      "position": i + 2,
      "name": obj.name ? obj.name : "",
      "item": obj.url ? `${rootDomain + obj.url}` : ""
    })
  })
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": url + "#breadcrumb",
    "name": "Navigation",
    "itemListElement": itemListArray
  }
}

const ProductSchema = (props) => {
  const { title, seoDescription, url, imgSrc, price, currency, sku, mpn, gtin } = props;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": url + "#Product",
    "url": url,
    "name": title ? title : "",
    "sku": sku ? sku : "",
    "mpn": mpn ? mpn : "",
    "gtin": gtin ? gtin : "",
    "description": seoDescription ? seoDescription() : "",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "price": price ? price : "",
      "priceCurrency": currency ? currency : "SEK",
      "url": url,
      "priceValidUntil": new Date(new Date().setDate(new Date().getDate() + 31)).toISOString(),
      "itemCondition": "https://schema.org/NewCondition",
      "seller": schemaOrganization
    },
    "image": [
      {
        "@type": "ImageObject",
        "@id": url + "#primaryimage",
        "url": imgSrc,
        "width": "1200",
        "height": "1200",
        "contentUrl": imgSrc,
        "caption": title
      }
    ],
    "potentialAction": {
      "@type": "ViewAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": url
      }
    }
  };
};

const WebSiteSchema = (props) => {
  const { title, seoDescription, url, lastReviewed, dateCreated, locale } = props;
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": url + "#website",
    "name": "Attitude.se Tryckeri",
    "alternateName": ["Attitude.se"],
    "url": "https://www.attitude.se/",
    "inLanguage": locale ? locale : "sv-SE",
    "description": seoDescription ? seoDescription() : "",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url + "#webpage"
    },
    ...schemaSearchBox
  };
};

const WebpageSchema = (props) => {
  const { title, seoDescription, url, lastReviewed, dateCreated, locale } = props;
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": url + "#webpage",
    "name": title ? title : "",
    "url": url,
    "lastReviewed": lastReviewed ? lastReviewed : new Date().toISOString(),
    "dateCreated": dateCreated ? dateCreated : new Date().toISOString(),
    "inLanguage": locale ? locale : "sv-SE",
    "description": seoDescription ? seoDescription() : "",
    "reviewedBy": schemaBrand,
    "speakable": {
      "@type": "SpeakableSpecification",
      "xPath": [
        "/html/head/title",
        "/html/head/meta[@name='description']"
      ]
    },
    "isPartOf": {
      "@type": "WebSite",
      "@id": rootDomain + "#website"
    }
  };
};


const ArticleSchema = (props) => {
  const { title, seoDescription, url, imgSrc, lastReviewed, dateCreated } = props
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "url": url,
    "headline": title ? title : "",
    "description": seoDescription ? seoDescription() : "",
    "datePublished": dateCreated ? dateCreated : new Date().toISOString(),
    "dateModified": lastReviewed ? lastReviewed : new Date().toISOString(),
    "author": schemaAuthor,
    "publisher": schemaOrganization,
    "image": [
      {
        "@type": "ImageObject",
        "@id": url + "#primaryimage",
        "url": imgSrc,
        "width": "1200",
        "height": "625"
      }
    ]
  }
}

const HowToOrderSchema = (props) => {
  const { name, price, order_min, imgSrc, url } = props
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "Hur man beställer tryck på " + name,
    "image": {
      "@type": "ImageObject",
      "url": imgSrc,
      "height": "600",
      "width": "600"
    },
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "SEK",
      "value": price * order_min
    },
    "step": [
      {
        "@type": "HowToStep",
        "name": "Välj en artikel för tryck och konfigurera den",
        "url": url + "#choose_color",
        "itemListElement": [{
          "@type": "HowToDirection",
          "text": "Klicka i en önskad färg och välj storlek/storlekar."
        }, {
          "@type": "HowToDirection",
          "text": "Välj antal artiklar som du behöver få tryckt."
        }, {
          "@type": "HowToDirection",
          "text": "Välj var på artikeln du vill få din design tryckt."
        }, {
          "@type": "HowToDirection",
          "text": "Ladda upp bilder och/eller logotyper."
        }, {
          "@type": "HowToDirection",
          "text": "Lämna ett meddelande till vår grafiker för bättre förståelse över din design."
        }],
        "image": {
          "@type": "ImageObject",
          "url": "https://storage.googleapis.com/attitude-firestore.appspot.com/logo/how-to-configure-article.png",
          "height": "834",
          "width": "1509"
        }
      }, {
        "@type": "HowToStep",
        "name": "Lägg en förfrågan och invänta korrektur från vår grafiker",
        "url": url,
        "itemListElement": [{
          "@type": "HowToDirection",
          "text": "För att kunna leverera bästa kvalitet för våra kunder skapar vi alltid ett korrektur, som våra kunder får godkänna, innan ordern går till produktion."
        }, {
          "@type": "HowToDirection",
          "text": "Invänta korrektur och följ instruktionerna för att godkänna korrekturen — om allt ser bra ut."
        }, {
          "@type": "HowToTip",
          "text": "Var noga att säkerställa att korrekturen är korrekt. Vi ansvarar ej över fel efter att korrekturen har blivit godkänd."
        }],
        "image": {
          "@type": "ImageObject",
          "url": "https://storage.googleapis.com/attitude-firestore.appspot.com/logo/how-to-wait-for-correction.png",
          "height": "780",
          "width": "801"
        }
      }, {
        "@type": "HowToStep",
        "name": "Få dina tryckta artiklar levererade",
        "url": url,
        "itemListElement": [{
          "@type": "HowToDirection",
          "text": "När vi har tryckt dina artiklar och de är påväg så skickar vi dig en spårningslänk."
        }, {
          "@type": "HowToDirection",
          "text": "Hämta dina tryckta artiklar!"
        }],
        "image": {
          "@type": "ImageObject",
          "url": "https://storage.googleapis.com/attitude-firestore.appspot.com/logo/how-to-order-delivered.png",
          "height": "835",
          "width": "1512"
        }
      }
    ],
    "totalTime": "P2W"
  }
}

const FaqSchema = (props) => {
  const { faqArray } = props
  let array = [{
    "@type": "Question",
    "name": "Varför skall du välja Attitude.se?",
    "acceptedAnswer": {
      "@type": "Answer",
      "text": `<ul>
        <li><b>Kvalitetssäkring: </b>Du godkänner alltid korrektur, gjord av en grafiker, innan produktion.</li>
        <li><b>Låga volymkrav: </b>Flera av våra artiklar har 1 artikel som minsta beställningsantal.</li>
        <li><b>Inga startavgifter: </b>I vår prissättning tillkommer inga startavgifter.</li>
        <li><b>Kläder trycks i Sverige: </b>Flera av våra kläder trycks i Sverige med hög kvalitet & låga felmarginaler.</li>
        <li><b>Fri frakt över 3000kr: </b>Leveranstid är ungefär 2 veckor men prata med oss om det är brådskande.</li>
        <li><b>Mängdrabatt: </b>Större antal artiklar per order ger dig lägre pris per artikel.</li>
      </ul>`
    }
  },
  {
    "@type": "Question",
    "name": "Kan man trycka på egna plagg?",
    "acceptedAnswer": {
      "@type": "Answer",
      "text": `<p>Ja, vi trycker på </p>`
    }
  }
  ]
  if (faqArray.length > 0) array = [...array, ...faqArray.map(faq => {
    return {
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }
  })]
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": array
  }
}

const schemaSearchBox = {
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": rootDomain + "/search?query={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  }
}

const schemaLocalBusiness = {
  "@context": "https://schema.org",
  "@type": ["Store", "Printing", "Wholesale"],
  "image": [
    "https://lh5.googleusercontent.com/p/AF1QipOoBzpsBn4UW6GdNd8WWs_Ycm4jr_8DWR21c3Ql=s883-k-no",
    "https://lh5.googleusercontent.com/p/AF1QipMCxywtsRn_PjOm-r_L2xcseKI-y352MZaHBYDz=w406-h304-k-no"
  ],
  "name": "Attitude.se",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Ingenjörsgatan 5",
    "addressLocality": "Göteborg",
    "addressRegion": "Västra Götaland",
    "postalCode": "41119",
    "addressCountry": "SE"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 57.703140,
    "longitude": 11.956910
  },
  "url": rootDomain,
  "priceRange": "$",
  "telephone": "+4631134141",
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday"
      ],
      "opens": "09:00",
      "closes": "15:00"
    }
  ]
}

const schemaAuthor = {
  "@context": "https://schema.org",
  "@type": "Person",
  "name": "Johannes Eklund",
  "url": "https://www.attitude.se/om-attitude",
  "image": {
    "@type": "ImageObject",
    "url": "https://1.gravatar.com/avatar/89097adc104e81cc3e038e27d06bab29",
    "height": 96,
    "width": 96
  }
}

const schemaOrganization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Attitude.se",
  "url": "https://www.attitude.se",
  "description": "Attitude.se — Vi trycker din design och logotyp på allt sedan 2003. Genom våra leverantörer kan vi erbjuda tryck på ett gigantisk sortiment av kläder, och annat, till riktigt bra priser.",
  "slogan": "Attitude.se — Vi trycker allt sedan 2003",
  "logo": "https://storage.googleapis.com/attitude-firestore.appspot.com/logo/attitude-logo-square.png",
  "sameAs": [
    "https://www.facebook.com/attitude.se/",
    "https://www.instagram.com/attitude.se/"
  ],
  "foundingDate": "2003",
  "founder": {
    "@type": "Person",
    "name": "Julian Burman"
  },
  "address": [{
    "@type": "PostalAddress",
    "streetAddress": "Ingenjörsgatan 5",
    "addressLocality": "Gothenburg",
    "addressRegion": "Västra Götaland",
    "postalCode": "41119",
    "addressCountry": "SE"
  }],
  "contactPoint": [{
    "@type": "ContactPoint",
    "telephone": "+46-31-134141",
    "contactType": "customer service",
    "areaServed": ["SE"],
    "availableLanguage": "Swedish",
    "contactOption": "TollFree"
  },
  {
    "@type": "ContactPoint",
    "telephone": "+46-31-134141",
    "contactType": "customer service",
    "areaServed": ["SE"],
    "availableLanguage": "English",
    "contactOption": "TollFree"
  }]
}

const schemaBrand = {
  "@context": "https://schema.org",
  "@type": "Brand",
  "name": "Attitude.se",
  "url": "https://www.attitude.se",
  "description": "Attitude.se — Vi trycker din design och logotyp på allt sedan 2003. Genom våra leverantörer kan vi erbjuda tryck på ett gigantisk sortiment av kläder, och annat, till riktigt bra priser.",
  "slogan": "Attitude.se — Experter på tryck sedan 2003",
  "logo": "https://storage.googleapis.com/attitude-firestore.appspot.com/logo/attitude-logo-square.png"
}

const VideoObjectSchema = {
  "@context": "https://schema.org",
  "@type": "VideoObject",
  "name": "Attitude.se — Såhär beställer du via vår hemsida.",
  "description": "Sedan 2003 har Attitude.se varit det självklara valet för företag och privatpersoner som söker högkvalitativa tryck på allt från kläder till kontorsmaterial, mässor och events.",
  "thumbnailUrl": "https://img.youtube.com/vi/pg14B0lI8Dw/0.jpg",
  "uploadDate": "2023-11-29T08:00:00+08:00",
  "contentUrl": "https://www.youtube.com/watch?v=pg14B0lI8Dw",
  "publisher": {
    "@type": "Organization",
    "name": "YouTube",
    "logo": {
      "@type": "ImageObject",
      "url": "https://www.youtube.com/about/static/svgs/icons/brand-resources/YouTube-logo-full_color_light.svg?cache=7c9f45d",
      "width": 282,
      "height": 60
    }
  },
  "author": schemaAuthor
};

const ShippingDetailSchema = (type) => {
  if (type === "pickup") return PickupSchema
  else if (type === "free") return FreeShippingSchema
  else if (type === "standard") return StandardShippingSchema
  else if (type === "express") return ExpressShippingSchema
}

export { itemListSchema, WebSiteSchema, WebpageSchema, ArticleSchema, ProductSchema, BreadcrumbSchema, SiteNavigationSchema, FaqSchema, schemaLocalBusiness, schemaAuthor, schemaOrganization, schemaBrand, HowToOrderSchema, VideoObjectSchema, ShippingDetailSchema }